/**
 * The two things that make an aeroplane *parked* rather than merely present:
 * it stands on its undercarriage, and it puts a shadow on the ground.
 *
 * ## Standing on its wheels
 *
 * `flight/aircraft-mesh.ts` returns a mesh recentred on its own bounding-box
 * centre — the right frame for a turntable and for everything the sim measures
 * against, and the wrong one for a parked aeroplane, whose contract is that its
 * *lowest* point touches the ground. So the lift is measured rather than
 * tabulated: take the world-space bounding box of whatever was loaded and raise
 * it by its own `-min.y`. That works for the baked `.TBL` and the enhanced
 * `.glb` alike, needs no per-airframe number, and is correct by construction for
 * a skid, a tailwheel or a nosewheel because it never asks which part is
 * lowest.
 *
 * ## The shadow
 *
 * The original draws one **on this screen**, and draws it big — and on this
 * screen only: `flight/ground-shadow.ts` records the count that settled the
 * flight views, where the original draws none at all, and the nineteen slots
 * below are never placed in any world. `PTPLANE.TBL` slots **13 to 31**
 * are nineteen flat aircraft plan-forms — 6.5 to 12.3 m across, every vertex at
 * z = 0, every face `BARON.PAL` **64** `#141414` — which is a set of ground
 * shadows, one per airframe, and nothing else. Below the skyline, where a
 * ground shadow is the only thing that colour can be, they are **4.29 % of
 * `docs/game/ui-reference/view-airplanes-3d.png`**.
 *
 * That share is the whole design note. A shadow dropped straight down from an
 * aeroplane standing on the ground lands *under* the aeroplane and is hidden by
 * it: built that way first, and measured on the real GPU, this one covered
 * **241 pixels** of an 840 x 380 view — geometrically right and visually
 * absent. The original's is thrown about 5 m toward the viewer, which is a low
 * sun; `parked-look.ts`'s `PARKED_SUN_ELEVATION_DEG` has that measurement, and
 * this module shears along it.
 *
 * The silhouette is derived from the model on screen rather than from the
 * nineteen baked slots, and that is deliberate: the slots do not cover the
 * twenty-two-scout roster this screen browses, and no decoding recovers which
 * slot belongs to which airframe. A projection is correct for every entry in
 * the roster, for an ace's livery, and for an enhanced `.glb` — none of which a
 * nineteen-slot lookup could be.
 *
 * `three/ramp-shade.ts`'s `SUN_DIRECTION_WORLD` is deliberately *not* used, and
 * neither is `flight/time-of-day.ts`'s. The first is the *shading* sun the
 * aeroplane's own faces are dotted against and it is horizontal (`[0, 0, -1]`),
 * a direction that projects nothing onto a ground plane at all; the second is
 * within a thousandth of the zenith at noon, which is the 241-pixel case. Three
 * different suns, and only one of them is about where a shadow lands.
 *
 * ## Depth
 *
 * Opaque, `depthWrite`/`depthTest` untouched, no `renderOrder`. It clears the
 * ground plane geometrically instead: `flight/world.ts` sinks that plane to
 * `-0.08` m, so a shadow at y = 0 sits 8 cm above it — the same separation
 * every placed `TERR` polygon already relies on, under the same reversed-Z
 * buffer. lessons.md records three separate outages caused by reaching for
 * `depthWrite: false` here instead; this reaches for the gap that already
 * exists.
 *
 * The projected triangles overlap each other (a wing lands on the fuselage's
 * own outline) and are exactly coplanar, which would be a depth tie if it could
 * be seen — it cannot, because every one of them is the same single colour, so
 * whichever wins paints the identical pixel.
 */

import * as THREE from "three/webgpu";
import { PARKED_SUN_ELEVATION_DEG, SHADOW_COLOUR } from "./parked-look.ts";
import { worldTrianglePositions } from "../three/silhouette-vertices.ts";

/**
 * Height the shadow is drawn at, metres.
 *
 * **Not zero, and the two centimetres are the whole reason it is on screen.**
 * The datum is already occupied: `three/world-instancer.ts` places
 * `PTPLANE-09`'s runway plate — the surface the aeroplane parks on — with all
 * of its geometry at exactly y = 0, because that is where the `.TBL` puts it.
 * A shadow drawn at the datum is therefore *coplanar with the runway*, which is
 * the one thing a depth buffer cannot arbitrate, and it lost: measured on the
 * real GPU, the shadow contributed **0 pixels** and the well's near-black count
 * stayed at the 240 px of the aeroplane's own tyres either side of the change.
 *
 * This is the tie lessons.md's z-fighting entry describes and it is resolved the
 * way that entry prefers — by removing it rather than by biasing a material
 * flag. Two centimetres is invisible at every step of the zoom ladder and
 * decisive under the reversed-Z buffer `parked-preview.ts` sets up; it also
 * clears `createGround`'s backdrop plane, which sits a further 8 cm below.
 */
export const SHADOW_Y_M = 0.02;

/**
 * Metres the shadow slides *toward the default viewpoint* per metre of height —
 * `cot(PARKED_SUN_ELEVATION_DEG)`, i.e. the shadow of a 2.3 m airframe reaches
 * about 5 m, which is what the original's frame measures. Positive is toward
 * -Z, the side the default camera stands on.
 */
export const SHADOW_REACH_PER_METRE = 1 / Math.tan((PARKED_SUN_ELEVATION_DEG * Math.PI) / 180);

/**
 * How far to raise `object` so its lowest point rests on y = 0, in metres.
 *
 * Reads the *world* box, so it is indifferent to where the -90 degree model-frame
 * rotation was applied — carried on the object's matrix for a baked mesh,
 * baked into the geometry for an enhanced one. Returns 0 for an object with no
 * renderable geometry rather than `Infinity`, so a failed load cannot launch
 * the aeroplane out of the scene.
 */
export function groundContactLift(object: THREE.Object3D): number {
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty() || !Number.isFinite(box.min.y)) return 0;
  return -box.min.y;
}

/**
 * The aeroplane's outline on the ground, or `undefined` where it has no
 * geometry to drop.
 *
 * The returned mesh is in world space already — add it to the scene root, not
 * to the aeroplane, or it would inherit the very lift that put the aeroplane
 * above it.
 */
export function createGroundShadow(object: THREE.Object3D): THREE.Mesh | undefined {
  const positions = worldTrianglePositions(object);
  if (positions.length < 9) return undefined;
  for (let i = 0; i < positions.length; i += 3) {
    const height = positions[i + 1] ?? 0;
    positions[i + 1] = SHADOW_Y_M;
    positions[i + 2] = (positions[i + 2] ?? 0) - height * SHADOW_REACH_PER_METRE;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  // Winding is whatever the drop left behind, so both faces are drawn — the
  // same reason every other baked-geometry material in this app is DoubleSide.
  const material = new THREE.MeshBasicMaterial({ color: SHADOW_COLOUR, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.matrixAutoUpdate = false;
  return mesh;
}
